import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Map, { Marker, Popup, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  Stethoscope, Heart, AlertTriangle, RefreshCw, Truck, MapPin, Filter,
  Users, ClipboardList, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { MAPBOX_TOKEN, DEFAULT_CENTER, VISIT_PRIORITY_COLORS, READINESS_COLORS, SOURCE_LABELS, timeAgo } from "@/pages/fleet/constants";

const REFRESH_MS = 30000;

function ClinicianPin({ clinician, onClick }) {
  const c = READINESS_COLORS[clinician.readiness] || READINESS_COLORS.unavailable;
  return (
    <button onClick={onClick} className="relative" data-testid={`fleet-clinician-pin-${clinician.clinician_id}`}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white"
        style={{ background: c.dot }}>
        <Stethoscope className="w-4 h-4 text-white" />
      </div>
      <div className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full ring-2 ring-white" style={{ background: c.dot }} />
    </button>
  );
}

function PatientPin({ patient, onClick }) {
  const c = VISIT_PRIORITY_COLORS[patient.highest_priority] || VISIT_PRIORITY_COLORS.routine;
  return (
    <button onClick={onClick} className="relative" data-testid={`fleet-patient-pin-${patient.mrn}`}>
      <div className="w-7 h-7 rounded-md flex items-center justify-center shadow-lg ring-2 ring-white"
        style={{ background: c.border }}>
        <Heart className="w-3.5 h-3.5 text-white" />
      </div>
      {patient.visit_count > 1 && (
        <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#0B7E8C] text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
          {patient.visit_count}
        </div>
      )}
    </button>
  );
}

function VehiclePin({ vehicle, onClick }) {
  return (
    <button onClick={onClick} className="relative" data-testid={`fleet-vehicle-pin-${vehicle.plate_number}`}>
      <div className="w-7 h-7 rounded-md flex items-center justify-center shadow-lg ring-2 ring-white bg-[#475569]">
        <Truck className="w-3.5 h-3.5 text-white" />
      </div>
    </button>
  );
}

export default function FleetMap() {
  const navigate = useNavigate();
  const [data, setData] = useState({ clinicians: [], patients: [], vehicles: [] });
  const [loading, setLoading] = useState(true);
  const [popup, setPopup] = useState(null);
  const [showClin, setShowClin] = useState(true);
  const [showPat, setShowPat] = useState(true);
  const [showVeh, setShowVeh] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Recommendations side-panel state
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [recBusy, setRecBusy] = useState(false);
  const [narrative, setNarrative] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/fleet/map");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  async function fetchRecommendations(visit) {
    setSelectedVisit(visit); setRecBusy(true); setRecommendations([]); setNarrative(null);
    try {
      const r = await api.post(`/fleet/visits/${visit.id}/recommend`);
      setRecommendations(r.data.recommendations || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRecBusy(false); }
  }
  async function fetchNarrative() {
    if (!selectedVisit) return;
    setRecBusy(true);
    try {
      const r = await api.post(`/fleet/visits/${selectedVisit.id}/recommend/explain`);
      setNarrative(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRecBusy(false); }
  }
  async function assignClinician(clinicianId, ai_accepted) {
    if (!selectedVisit) return;
    try {
      await api.post(`/fleet/visits/${selectedVisit.id}/assign`, { clinician_id: clinicianId, ai_accepted });
      toast.success("Visit assigned");
      setSelectedVisit(null); setRecommendations([]); setNarrative(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (!MAPBOX_TOKEN) {
    return (
      <div className="hah-card p-8 text-center text-sm text-[#6B7280]">
        Mapbox token not configured. Set REACT_APP_MAPBOX_TOKEN in frontend/.env
      </div>
    );
  }

  return (
    <div data-testid="fleet-map-page">
      <PageHeader
        title="Live Operations Map"
        subtitle="Real-time positions for clinicians, patients with active visits, and fleet vehicles."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setAutoRefresh((a) => !a)} className="border-[#E5E7EB]" data-testid="fleet-map-autorefresh">
              {autoRefresh ? "Auto ON" : "Auto OFF"}
            </Button>
            <Button variant="outline" onClick={load} className="border-[#E5E7EB]" data-testid="fleet-map-refresh">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
          </div>
        }
      />

      <div className="hah-card p-3 mb-3 flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]"><Filter className="w-3.5 h-3.5 inline mr-1" />Layers:</span>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={showClin} onChange={(e) => setShowClin(e.target.checked)} data-testid="fleet-map-toggle-clinicians" />
          <Stethoscope className="w-3.5 h-3.5 text-[#0B7E8C]" /> Clinicians ({data.clinicians.length})
        </label>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={showPat} onChange={(e) => setShowPat(e.target.checked)} data-testid="fleet-map-toggle-patients" />
          <Heart className="w-3.5 h-3.5 text-[#EF4444]" /> Patients ({data.patients.length})
        </label>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={showVeh} onChange={(e) => setShowVeh(e.target.checked)} data-testid="fleet-map-toggle-vehicles" />
          <Truck className="w-3.5 h-3.5 text-[#475569]" /> Vehicles ({data.vehicles.length})
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* MAP */}
        <div className="lg:col-span-2 hah-card overflow-hidden" style={{ height: "640px" }}>
          <Map
            mapboxAccessToken={MAPBOX_TOKEN}
            initialViewState={DEFAULT_CENTER}
            mapStyle="mapbox://styles/mapbox/light-v11"
            style={{ width: "100%", height: "100%" }}
          >
            <NavigationControl position="top-right" />
            {showClin && data.clinicians.filter((c) => c.latitude && c.longitude).map((c) => (
              <Marker key={c.clinician_id} longitude={c.longitude} latitude={c.latitude} anchor="center">
                <ClinicianPin clinician={c} onClick={() => setPopup({ kind: "clinician", item: c })} />
              </Marker>
            ))}
            {showPat && data.patients.map((p) => (
              <Marker key={p.patient_id} longitude={p.longitude} latitude={p.latitude} anchor="center">
                <PatientPin patient={p} onClick={() => setPopup({ kind: "patient", item: p })} />
              </Marker>
            ))}
            {showVeh && data.vehicles.map((v) => (
              <Marker key={v.id} longitude={v.longitude} latitude={v.latitude} anchor="center">
                <VehiclePin vehicle={v} onClick={() => setPopup({ kind: "vehicle", item: v })} />
              </Marker>
            ))}
            {popup && (
              <Popup
                longitude={popup.item.longitude}
                latitude={popup.item.latitude}
                anchor="top"
                onClose={() => setPopup(null)}
                closeButton
                maxWidth="260px"
              >
                {popup.kind === "clinician" && (
                  <div className="p-1 min-w-[200px]" data-testid="fleet-popup-clinician">
                    <div className="text-sm font-semibold text-[#1F2937]">{popup.item.name}</div>
                    <div className="text-xs text-[#6B7280] capitalize">{(popup.item.role_code || "").replace("_", " ")} · {popup.item.facility_name}</div>
                    <div className="text-xs mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                      style={{ background: (READINESS_COLORS[popup.item.readiness] || {}).bg, color: (READINESS_COLORS[popup.item.readiness] || {}).text }}>
                      {(READINESS_COLORS[popup.item.readiness] || {}).label}
                    </div>
                    <div className="text-xs text-[#6B7280] mt-1">Workload: {popup.item.workload}/{popup.item.capacity || 6}</div>
                    <div className="text-[10px] text-[#94A3B8] mt-1">Updated {timeAgo(popup.item.updated_at)}</div>
                  </div>
                )}
                {popup.kind === "patient" && (
                  <div className="p-1 min-w-[220px]" data-testid="fleet-popup-patient">
                    <div className="text-sm font-semibold text-[#1F2937]">{popup.item.name}</div>
                    <div className="text-xs text-[#6B7280]">{popup.item.mrn}</div>
                    <div className="text-xs mt-2 font-semibold text-[#475569]">{popup.item.visit_count} active visit(s):</div>
                    <div className="space-y-1 mt-1">
                      {popup.item.visits.map((v) => (
                        <div key={v.id} className="text-xs flex items-center justify-between gap-2">
                          <span className="text-[#1F2937]">{v.type.replace(/_/g, " ")}</span>
                          <button onClick={() => { setPopup(null); fetchRecommendations(v); }}
                            className="text-[#0B7E8C] hover:underline inline-flex items-center gap-0.5"
                            data-testid={`fleet-popup-recommend-${v.id}`}>
                            Recommend <ChevronRight className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {popup.kind === "vehicle" && (
                  <div className="p-1 min-w-[160px]" data-testid="fleet-popup-vehicle">
                    <div className="text-sm font-semibold text-[#1F2937]">{popup.item.plate_number}</div>
                    <div className="text-xs text-[#6B7280] capitalize">{popup.item.vehicle_type} · {popup.item.status}</div>
                  </div>
                )}
              </Popup>
            )}
          </Map>
        </div>

        {/* SIDE PANEL */}
        <div className="hah-card p-4 max-h-[640px] overflow-auto" data-testid="fleet-side-panel">
          {!selectedVisit ? (
            <div>
              <div className="hah-section-title">Quick stats</div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="rounded-lg bg-[#F0FDF4] border border-[#10B981]/30 p-2">
                  <div className="text-[10px] uppercase font-semibold text-[#065F46]">Clinicians</div>
                  <div className="text-xl font-bold text-[#065F46]">{data.clinicians.length}</div>
                </div>
                <div className="rounded-lg bg-[#FEF2F2] border border-[#EF4444]/30 p-2">
                  <div className="text-[10px] uppercase font-semibold text-[#991B1B]">Patient visits</div>
                  <div className="text-xl font-bold text-[#991B1B]">{data.patients.reduce((a, b) => a + b.visit_count, 0)}</div>
                </div>
                <div className="rounded-lg bg-[#F8FAFC] border border-[#94A3B8]/30 p-2">
                  <div className="text-[10px] uppercase font-semibold text-[#475569]">Vehicles</div>
                  <div className="text-xl font-bold text-[#475569]">{data.vehicles.length}</div>
                </div>
                <div className="rounded-lg bg-[#FFF7ED] border border-[#F97316]/30 p-2">
                  <div className="text-[10px] uppercase font-semibold text-[#9A3412]">Urgent visits</div>
                  <div className="text-xl font-bold text-[#9A3412]">{data.patients.filter((p) => p.highest_priority !== "routine").length}</div>
                </div>
              </div>
              <div className="mt-4 text-xs text-[#6B7280]">Click any patient pin → <strong>Recommend</strong> to see AI clinician suggestions.</div>
              <div className="mt-4">
                <Button variant="outline" onClick={() => navigate("/fleet/visits")} className="w-full border-[#E5E7EB]">
                  <ClipboardList className="w-4 h-4 mr-1.5" /> Open Visit Scheduling Board
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="hah-section-title">AI Recommendations</div>
                  <h3 className="text-sm font-semibold text-[#1F2937] mt-0.5 capitalize">{selectedVisit.type.replace(/_/g, " ")}</h3>
                  <div className="text-xs text-[#6B7280]">Priority: {selectedVisit.priority}</div>
                </div>
                <button onClick={() => { setSelectedVisit(null); setRecommendations([]); setNarrative(null); }}
                  className="text-xs text-[#6B7280] hover:text-[#0B7E8C]">Close</button>
              </div>
              {recBusy && <div className="text-xs text-[#6B7280] py-3">Computing recommendations…</div>}
              {!recBusy && recommendations.length === 0 && (
                <div className="text-xs text-[#94A3B8] italic py-3">No recommendations available.</div>
              )}
              <div className="space-y-2 mt-2">
                {recommendations.map((r, idx) => (
                  <div key={r.clinician_id} className={`rounded-lg border p-2.5 ${idx === 0 ? "border-[#0B7E8C] bg-[#EAF8FA]" : "border-[#E5E7EB] bg-white"}`}
                    data-testid={`fleet-rec-${idx}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div>
                        <div className="text-sm font-semibold text-[#1F2937]">{r.clinician_name || r.clinician_id}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{(r.role_code || "").replace("_", " ")}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-[#0B7E8C]">{r.score}</div>
                        <div className="text-[10px] text-[#6B7280]">score</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px] text-[#475569] mb-2">
                      <span><MapPin className="w-3 h-3 inline mr-0.5" />{r.distance_km}km · {r.eta_minutes}min</span>
                      <span>Load {r.workload_pct}%</span>
                      <span style={{ color: (READINESS_COLORS[r.readiness] || {}).text }}>{(READINESS_COLORS[r.readiness] || {}).label}</span>
                    </div>
                    <div className="text-[10px] text-[#6B7280] mb-2 line-clamp-2">{r.reasons?.join(" · ")}</div>
                    <Button size="sm" onClick={() => assignClinician(r.clinician_id, idx === 0)}
                      className="w-full bg-[#0B7E8C] hover:bg-[#128C97] text-white h-7 text-xs"
                      data-testid={`fleet-rec-assign-${idx}`}>
                      Assign {idx === 0 ? "(accept AI)" : ""}
                    </Button>
                  </div>
                ))}
              </div>
              {recommendations.length > 0 && (
                <div className="mt-3">
                  <Button size="sm" variant="outline" onClick={fetchNarrative} disabled={recBusy}
                    className="w-full border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#F5F3FF]" data-testid="fleet-rec-explain">
                    Why this pick? (LLM)
                  </Button>
                </div>
              )}
              {narrative && (
                <div className="mt-2 p-2.5 rounded bg-[#F5F3FF] border border-[#8B5CF6]/30 text-xs text-[#1F2937]" data-testid="fleet-rec-narrative">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#8B5CF6] mb-1">
                    {narrative.model === "fallback" ? "Deterministic" : narrative.model}
                  </div>
                  {narrative.narrative}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
